// Third Party
import React from 'react';
import classNames from 'classnames';

// Components
import { HoldingIcon } from '../../../components/common';
import ComparisonBaseCell from '../ComparisonTable/ComparisonBaseCell';
import { IconButton } from '../../../components/buttons';

// Utils
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import { GIF_PORTFOLIO_COLOR, BMOi18n } from '../../../../utils/bmo-gif/constants';
import { formatCurrency } from '../../../../utils/i18n';
import { getAnnualData } from '../../../../utils/bmo-gif/utils';
import { getLumpSumInfo, getSystematicInfo } from '../../../../utils/bmo-gif/index';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOGIFScenarioData from '@/types/bmo-gif/bmo-gif-scenario-data';

type Props = {
  className?: string;
  item: BMOGIFItem;
  scenario: BMOGIFScenario;
  locale: string;
  createNewScenario: () => void;
  loadScenarioForEdit: (scenario: BMOGIFScenario) => void;
  scenarioNumber?: number;
  deleteScenario: (scenario: BMOGIFScenario) => void;
  selectScenario: (scenario: BMOGIFScenario) => void;
  selected: boolean;
  portfolioIsFull: boolean;
  readOnly?: boolean;
  showGraph: boolean;
};

class BMOGIFComparisonScenario extends React.PureComponent<Props> {
  static defaultProps = {
    createNewScenario: () => {},
    loadScenarioForEdit: () => {},
    deleteScenario: () => {},
  };

  renderContent = () => {
    const { scenario } = this.props;

    return (
      scenario.data &&
      getAnnualData(scenario.data, scenario.purchaseDate).map((dataRow: BMOGIFScenarioData) => (
        <div key={new Date(dataRow.date).getTime()} className="data-row d-flex">
          <ComparisonBaseCell>{dataRow.age}</ComparisonBaseCell>

          <ComparisonBaseCell>{formatCurrency(dataRow.portfolioValue, 0, undefined, false)}</ComparisonBaseCell>

          {scenario.withdrawal && <ComparisonBaseCell>{formatCurrency(dataRow.netWithdrawal, 0, undefined, false)}</ComparisonBaseCell>}

          {scenario.deposit.amount > 0 && <ComparisonBaseCell>{formatCurrency(dataRow.netDeposit, 0, undefined, false)}</ComparisonBaseCell>}

          <ComparisonBaseCell>{formatCurrency(dataRow.maturityGuarantee, 0, undefined, false)}</ComparisonBaseCell>

          <ComparisonBaseCell>{formatCurrency(dataRow.deathBenefitGuarantee, 0, undefined, false)}</ComparisonBaseCell>
        </div>
      ))
    );
  };

  onCreateNewScenario = (event: React.MouseEvent) => {
    this.props.createNewScenario();
    event.stopPropagation();
  };

  onEditScenario = (event: React.MouseEvent) => {
    const { scenario } = this.props;

    this.props.loadScenarioForEdit(scenario);
    event.stopPropagation();
  };

  onDeleteScenario = (event: React.MouseEvent) => {
    const { scenario } = this.props;

    this.props.deleteScenario(scenario);
    event.stopPropagation();
  };

  renderActionButtons = () => {
    const { scenario, locale, item, readOnly, portfolioIsFull } = this.props;

    const isDisabled = item.scenarios.length === 5;
    const tooltipMessage = isDisabled ? BMOi18n[locale].SCENARIOS.CREATE_DISABLED : BMOi18n[locale].SCENARIOS.CREATE_SCENARIO;

    if (readOnly || !portfolioIsFull) {
      return null;
    }

    if (!scenario.withdrawal) {
      return (
        <IconButton
          icon="plus-med"
          onClick={this.onCreateNewScenario}
          tooltip={tooltipMessage}
          tooltipPlace="top"
          tooltipEffect="solid"
          disabled={item.scenarios.length === 5}
        />
      );
    }

    return (
      <>
        <IconButton icon="pencil" onClick={this.onEditScenario} tooltip={BMOi18n[locale].SCENARIOS.EDIT_SCENARIO} tooltipPlace="top" tooltipEffect="solid" />
        <IconButton
          icon="ds-delete"
          onClick={this.onDeleteScenario}
          tooltip={BMOi18n[locale].SCENARIOS.DELETE_SCENARIO}
          tooltipPlace="top"
          tooltipEffect="solid"
        />
      </>
    );
  };

  renderHeader = () => {
    const { item, scenario, scenarioNumber, locale } = this.props;

    if (!scenario.withdrawal) {
      return (
        <>
          {item.portfolio.holdings.length > 0 && (
            <div className="gif-header-title">
              <HoldingIcon type={COMPARISON_ITEM_TYPES.PORTFOLIO} color={GIF_PORTFOLIO_COLOR} />
              <span className="gif-portfolio-symbols">
                {item.portfolio.holdings
                  .map(h => h.symbol)
                  .filter(s => s != null)
                  .join(', ')}
              </span>
            </div>
          )}

          <div className="gif-portfolio-name">
            <span data-private data-dd-privacy="mask">
              {item.name}
            </span>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="gif-header-title">
          <HoldingIcon type={COMPARISON_ITEM_TYPES.PORTFOLIO} color={GIF_PORTFOLIO_COLOR} />
          <span>{`${BMOi18n[locale].SCENARIOS.SCENARIO} ${scenarioNumber}`}</span>
        </div>

        <div className="withdrawal-info">
          <span className="lump-sum">{BMOi18n[locale].SCENARIOS.LUMP_SUM_WITHDRAWAL}</span>

          <span>{getLumpSumInfo(scenario, locale)}</span>

          <span className="systematic-withdrawal">{BMOi18n[locale].SCENARIOS.SYSTEMATIC_WITHDRAWAL}</span>

          <span>{getSystematicInfo(scenario, item, locale)}</span>
        </div>
      </>
    );
  };

  noSelection = () => {
    const { portfolioIsFull, showGraph, item } = this.props;

    return !portfolioIsFull || !showGraph || !item.scenarios.filter(s => !s._id.startsWith('new-')).length;
  };

  onSelectScenario = () => {
    const { scenario } = this.props;

    if (this.noSelection()) {
      return;
    }

    this.props.selectScenario(scenario);
  };

  render() {
    const { locale, scenario, selected, className } = this.props;

    const classes = classNames('bmo-gif-comparison-scenario', className, {
      selected,
      'no-selection': this.noSelection(),
    });

    return (
      <div className={classes} onClick={this.onSelectScenario}>
        <div className="gif-header">
          <div className="d-flex flex-column flex-fill">{this.renderHeader()}</div>

          {this.renderActionButtons()}
        </div>

        <div className="section-titles">
          <ComparisonBaseCell>
            <div>{BMOi18n[locale].SCENARIOS.AGE}</div>
          </ComparisonBaseCell>

          <ComparisonBaseCell>
            <div>{BMOi18n[locale].SCENARIOS.PORTFOLIO_VALUE}</div>
          </ComparisonBaseCell>

          {scenario.withdrawal && (
            <ComparisonBaseCell>
              <div>{BMOi18n[locale].SCENARIOS.NET_WITHDRAWAL}</div>
            </ComparisonBaseCell>
          )}

          {scenario.deposit.amount > 0 && (
            <ComparisonBaseCell>
              <div>{BMOi18n[locale].SCENARIOS.SUBSEQUENT_DEPOSIT}</div>
            </ComparisonBaseCell>
          )}

          <ComparisonBaseCell>
            <div>{BMOi18n[locale].SCENARIOS.MATURITY_GUARANTEE}</div>
          </ComparisonBaseCell>

          <ComparisonBaseCell>
            <div>{BMOi18n[locale].SCENARIOS.DEATH_BENEFIT_GUARANTEE}</div>
          </ComparisonBaseCell>
        </div>

        <div className="scenario-content">{this.renderContent()}</div>
      </div>
    );
  }
}

export default BMOGIFComparisonScenario;
